<template>
  <section class="auth-page">
    <b-container class="auth-container">
      <b-card class="auth-card">
        <h1 class="auth-title">Log in</h1>
        <p class="auth-subtitle">Welcome back. Continue your APWH practice.</p>

        <b-alert v-if="errorMessage" show variant="danger" class="mb-3">
          {{ errorMessage }}
        </b-alert>

        <b-form @submit.prevent="handleLogin">
          <b-form-group label="Email" label-for="login-email">
            <b-form-input
              id="login-email"
              v-model.trim="email"
              type="email"
              required
              autocomplete="email"
              placeholder="you@example.com"
            />
          </b-form-group>

          <b-form-group label="Password" label-for="login-password">
            <b-input-group>
              <b-form-input
                id="login-password"
                v-model="password"
                :type="passwordVisible ? 'text' : 'password'"
                required
                autocomplete="current-password"
                placeholder="Enter your password"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  type="button"
                  @click="passwordVisible = !passwordVisible"
                >
                  {{ passwordVisible ? "Hide" : "Show" }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>

          <b-button type="submit" variant="primary" class="w-100" :disabled="submitting">
            {{ submitting ? "Logging in..." : "Log in" }}
          </b-button>
        </b-form>

        <div class="auth-links mt-3">
          <router-link :to="forgotLink">Forgot your password?</router-link>
        </div>
        <div class="auth-links mt-2">
          New here?
          <router-link :to="signupLink">Create your free account</router-link>
        </div>
      </b-card>
    </b-container>
  </section>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { logInUser } from "@/services/authService";
import { trackEvent } from "@/services/analyticsService";

export default {
  name: "LoginView",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const email = ref("");
    const password = ref("");
    const passwordVisible = ref(false);
    const submitting = ref(false);
    const errorMessage = ref("");

    const redirectTarget = computed(() => {
      return typeof route.query.redirect === "string"
        ? route.query.redirect
        : "/account";
    });

    const signupLink = computed(() => ({
      name: "Signup",
      query: route.query.redirect ? { redirect: route.query.redirect } : {}
    }));

    const forgotLink = computed(() => ({
      name: "ForgotPassword",
      query: route.query.redirect ? { redirect: route.query.redirect } : {}
    }));

    const clearBodyScrollLock = () => {
      if (typeof document === "undefined") {
        return;
      }

      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
    };

    onMounted(() => {
      clearBodyScrollLock();
    });

    const handleLogin = async () => {
      trackEvent("login_started", {
        entry_path: route.fullPath
      });

      submitting.value = true;
      errorMessage.value = "";

      try {
        await logInUser(email.value, password.value);
        trackEvent("login_completed", {
          redirect_target: redirectTarget.value
        });
        await router.push(redirectTarget.value);
      } catch (error) {
        errorMessage.value = error.message || "Unable to log in right now.";
      } finally {
        submitting.value = false;
      }
    };

    return {
      email,
      password,
      passwordVisible,
      submitting,
      errorMessage,
      signupLink,
      forgotLink,
      handleLogin
    };
  }
};
</script>

<style scoped>
.auth-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  min-height: 100vh;
  width: 100%;
}

.auth-container {
  max-width: 520px;
}

.auth-card {
  border-radius: 14px;
  border: 1px solid #ececec;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  height: auto;
}

.auth-title {
  font-size: 32px;
  margin-bottom: 8px;
}

.auth-subtitle {
  color: #5b616e;
  margin-bottom: 16px;
}

.auth-links {
  font-size: 14px;
}
</style>
